		import { CheckIcon, ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import * as RPNInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import { Button } from '#app/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '#app/components/ui/command'
import { Input } from '#app/components/ui/input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover'
import { ScrollArea } from '#app/components/ui/scroll-area'
import { cn } from '#app/utils/misc'

type PhoneInputProps = Omit<
	React.ComponentProps<'input'>,
	'onChange' | 'value' | 'ref'
> &
	Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
		onChange?: (value: RPNInput.Value) => void
	}

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
	React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
		({ className, onChange, value, limitMaxLength = true, ...props }, ref) => {
			return (
				<RPNInput.default
					ref={ref}
					className={cn('flex w-full', className)}
					flagComponent={FlagComponent}
					countrySelectComponent={CountrySelect}
					inputComponent={InputComponent}
					smartCaret
					limitMaxLength={limitMaxLength}
					value={value || undefined}
					/**
					 * Handles the onChange event.
					 *
					 * react-phone-number-input might trigger the onChange event as undefined
					 * when a valid phone number is not entered. To prevent this,
					 * the value is coerced to an empty string.
					 *
					 * @param {E164Number | undefined} value - The entered value
					 */
					onChange={(value) => onChange?.(value || ('' as RPNInput.Value))}
					{...props}
				/>
			)
		},
	)
PhoneInput.displayName = 'PhoneInput'

const InputComponent = React.forwardRef<
	HTMLInputElement,
	React.ComponentProps<'input'>
>(({ className, ...props }, ref) => (
	<Input
		className={cn('w-full flex-1 rounded-s-none rounded-e-lg', className)}
		{...props}
		ref={ref}
	/>
))
InputComponent.displayName = 'InputComponent'

type CountryEntry = { label: string; value: RPNInput.Country | undefined }

type CountrySelectProps = {
	disabled?: boolean
	value: RPNInput.Country | undefined
	options: CountryEntry[]
	onChange: (country: RPNInput.Country) => void
}

const CountrySelect = ({
	disabled,
	value: selectedCountry,
	options: countryList,
	onChange,
}: CountrySelectProps) => {
	const scrollAreaRef = React.useRef<HTMLDivElement>(null)
	const [searchValue, setSearchValue] = React.useState('')
	const [isOpen, setIsOpen] = React.useState(false)

	return (
		<Popover
			open={isOpen}
			modal
			onOpenChange={(open) => {
				setIsOpen(open)
				open && setSearchValue('')
			}}
		>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					className="flex gap-1 rounded-s-lg rounded-e-none border-r-0 px-3 focus:z-10"
					disabled={disabled}
				>
					{selectedCountry && (
						<FlagComponent
							country={selectedCountry}
							countryName={selectedCountry}
						/>
					)}
					{!selectedCountry && (
						<span className="bg-foreground/20 flex h-4 w-6 overflow-hidden rounded-sm" />
					)}
					<span className="text-sm font-medium">
						+
						{selectedCountry
							? RPNInput.getCountryCallingCode(selectedCountry)
							: '1'}
					</span>
					<ChevronsUpDown
						className={cn(
							'-mr-2 size-4 opacity-50',
							disabled ? 'hidden' : 'opacity-100',
						)}
					/>
				</Button>
			</PopoverTrigger>
			{/* above full-screen windows too (the set-up window is z-100) */}
			<PopoverContent className="z-[200] w-[300px] overflow-hidden rounded-3xl bg-[#FBFDFD] p-0">
				<Command className="bg-transparent">
					<CommandInput
						value={searchValue}
						onValueChange={(value) => {
							setSearchValue(value)
							setTimeout(() => {
								if (scrollAreaRef.current) {
									const viewportElement = scrollAreaRef.current.querySelector(
											'[data-radix-scroll-area-viewport]',
										)
									if (viewportElement) {
										viewportElement.scrollTop = 0
									}
								}
							}, 0)
						}}
						placeholder="Search country..."
					/>
					<CommandList>
						<ScrollArea ref={scrollAreaRef} className="relative h-50 md:h-72">
							<CommandEmpty>No country found.</CommandEmpty>
							<CommandGroup>
								{countryList.map(({ value, label }) =>
									value ? (
										<CountrySelectOption
											key={value}
											country={value}
											countryName={label}
											selectedCountry={selectedCountry}
											onChange={onChange}
											onSelectComplete={() => setIsOpen(false)}
										/>
									) : null,
								)}
							</CommandGroup>
						</ScrollArea>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

interface CountrySelectOptionProps extends RPNInput.FlagProps {
	selectedCountry: RPNInput.Country | undefined
	onChange: (country: RPNInput.Country) => void
	onSelectComplete: () => void
}

const CountrySelectOption = ({
	country,
	countryName,
	selectedCountry,
	onChange,
	onSelectComplete,
}: CountrySelectOptionProps) => {
	const handleSelect = () => {
		onChange(country)
		onSelectComplete()
	}

	return (
		<CommandItem className="gap-2" onSelect={handleSelect}>
			<FlagComponent country={country} countryName={countryName} />
			<span className="flex-1 text-sm">{countryName}</span>
			<span className="text-foreground/50 text-sm">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
			{/* Radio-style indicator: an empty circle on every row, filled +
			    checked for the selected country. */}
			<span
				className={cn(
					'ml-auto flex size-4.5 shrink-0 items-center justify-center rounded-full border transition-colors',
					country === selectedCountry
						? 'border-[#32302F] bg-[#32302F]'
						: 'border-[#D5D4DF]',
				)}
			>
				{country === selectedCountry && (
					<CheckIcon className="size-2.5 text-white" />
				)}
			</span>
		</CommandItem>
	)
}

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
	const Flag = flags[country]

	return (
		<span className="bg-foreground/20 flex h-4 w-6 overflow-hidden rounded-sm [&_svg:not([class*='size-'])]:size-full">
			{Flag && <Flag title={countryName} />}
		</span>
	)
}

export { PhoneInput }
